import { createSelector } from 'reselect';
import { RootState } from 'store/types';
import { initialState } from 'store/app/reducer';
import { Alert, State } from 'store/app/types';
import {
  Estacion,
  FechaMaximaPractica,
  FechaMinimaPractica,
  JornadaUsuario,
} from 'store/practicas/types';
import { getTimeFromDate, removeTime } from '../../utils/dateTimeUtils';
import { makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected } from 'store/usuarios/selectors';
import { DatosPerfilesUsuario } from 'store/usuarios/types';

const selectState = (state: RootState) => state.app || initialState;

// Directly
export const makeSelectError = () =>
  createSelector(selectState, (state: State): string => state.error);

export const makeSelectUsername = () =>
  createSelector(selectState, (state: State): string => state.username);

export const makeSelectPassword = () =>
  createSelector(selectState, (state: State): string => state.password);

export const makeSelectDateMinSelected = () =>
  createSelector(
    selectState,
    (state: State): FechaMinimaPractica => state.dateMinSelected
  );

export const makeSelectDateMaxSelected = () =>
  createSelector(
    selectState,
    (state: State): FechaMaximaPractica => state.dateMaxSelected
  );

export const makeSelectStatePracticeSelected = () =>
  createSelector(
    selectState,
    (state: State): string => state.statePracticeSelected
  );

export const makeSelectCoursesSelected = () =>
  createSelector(
    selectState,
    (state: State): number[] => state.selectedCourses || []
  );

export const makeSelectCourseCreateSelected = () =>
  createSelector(
    selectState,
    (state: State): string => state.selectedCourse || ''
  );

export const makeSelectCourseSelected = () =>
  createSelector(
    makeSelectCoursesSelected(),
    (selectedCourses: number[]): string =>
      selectedCourses && Array.isArray(selectedCourses)
        ? selectedCourses.join()
        : selectedCourses
  );

export const makeSelectSocietiesSelected = () =>
  createSelector(
    selectState,
    (state: State): string => state.selectedSocieties
  );

export const makeSelectInstructorsSelected = () =>
  createSelector(
    selectState,
    (state: State): string[] => state.selectedInstructors || []
  );

export const makeSelectSelectedStudents = () =>
  createSelector(
    selectState,
    (state: State): string[] => state.selectedAlumnos
  );

export const makeSelectProfileSelected = () =>
  createSelector(selectState, (state: State): number => state.profileSelected);

export const makeSelectInfoUserProfileSelected = () =>
  createSelector(
    selectState,
    (state: State): any => state.infoUserProfileStatus
  );

export const makeSelectSelectedProvince = () =>
  createSelector(
    selectState,
    (state: State): string[] => state.selectedProvince
  );

export const makeSelectPracticaAuditoriaSeleccionada = () =>
  createSelector(
    selectState,
    (state: State): string => state.practicaAuditoriaSeleccionada
  );

export const makeSelectPracticaReclamadaSeleccionada = () =>
  createSelector(
    selectState,
    (state: State): string => state.practicaReclamadaSeleccionada
  );

export const makeSelectPracticaRechazadaSeleccionada = () =>
  createSelector(
    selectState,
    (state: State): string => state.practicaRechazadaSeleccionada
  );

export const makeSelectPracticesValidSelect = () =>
  createSelector(
    selectState,
    (state: State): string => state.practiceValidSelect
  );
  

export const makeSelectSelectedJourney = () =>
  createSelector(
    selectState,
    (state: State): JornadaUsuario | null => state.selectedJourney
  );

export const makeSelectStartDateTimeSelectedJourney = () =>
  createSelector(selectState, (state: State): Date | null => {
    return state.startDateTimeSelectedJourney;
  });
export const makeSelectStartTimeSelectedJourneyPristine = () =>
  createSelector(selectState, (state: State): boolean => {
    return state.startTimeSelectedJourneyPristine;
  });
export const makeSelectStartTimeStampSelectedJourney = () =>
  createSelector(selectState, (state: State): number | null => {
    return state.startDateTimeSelectedJourney
      ? new Date(state.startDateTimeSelectedJourney).getTime()
      : null;
  });
export const makeSelectStartDateTimeStampSelectedJourney = () =>
  createSelector(selectState, (state: State): number | null => {
    if (state.startDateTimeSelectedJourney) {
      const date: Date = new Date(state.startDateTimeSelectedJourney);
      removeTime(date);
      return date.getTime();
    } else {
      return null;
    }
  });
export const makeSelectStartTimeSelectedJourney = () =>
  createSelector(selectState, (state: State): string | null =>
    !!state.startDateTimeSelectedJourney
      ? getTimeFromDate(new Date(state.startDateTimeSelectedJourney), true)
      : null
  );
export const makeSelectFinishDateTimeSelectedJourney = () =>
  createSelector(
    selectState,
    (state: State): Date | null => state.finishDateTimeSelectedJourney
  );
export const makeSelectFinishTimeSelectedJourneyPristine = () =>
  createSelector(
    selectState,
    (state: State): boolean => state.finishTimeSelectedJourneyPristine
  );
export const makeSelectFinishTimeStampSelectedJourney = () =>
  createSelector(selectState, (state: State): number | null =>
    state.finishDateTimeSelectedJourney
      ? new Date(state.finishDateTimeSelectedJourney).getTime()
      : null
  );
export const makeSelectFinishDateTimeStampSelectedJourney = () =>
  createSelector(selectState, (state: State): number | null => {
    if (state.finishDateTimeSelectedJourney) {
      const date: Date = new Date(state.finishDateTimeSelectedJourney);
      removeTime(date);
      return date.getTime();
    } else {
      return null;
    }
  });
export const makeSelectFinishTimeSelectedJourney = () =>
  createSelector(selectState, (state: State): string | null =>
    state.finishDateTimeSelectedJourney
      ? getTimeFromDate(new Date(state.finishDateTimeSelectedJourney), true)
      : null
  );
export const makeSelectStartDateTimeSelectedPreparation = () =>
  createSelector(
    selectState,
    (state: State): Date | null => state.startDateTimeSelectedPreparation
  );
export const makeSelectStartTimeSelectedPreparationPristine = () =>
  createSelector(
    selectState,
    (state: State): boolean => state.startTimeSelectedPreparationPristine
  );
export const makeSelectStartDateTimeSelectedService = () =>
  createSelector(
    selectState,
    (state: State): Date | null => state.startDateTimeSelectedService
  );
export const makeSelectStartTimeSelectedServicePristine = () =>
  createSelector(
    selectState,
    (state: State): boolean => state.startTimeSelectedServicePristine
  );
export const makeSelectStartTimeStampSelectedService = () =>
  createSelector(
    selectState,
    (state: State): number | null =>
      state.startDateTimeSelectedService?.getTime() || null
  );
export const makeSelectStartDateTimeStampSelectedService = () =>
  createSelector(selectState, (state: State): number | null => {
    if (state.startDateTimeSelectedService) {
      const date: Date = new Date(state.startDateTimeSelectedService);
      removeTime(date);
      return date.getTime();
    } else {
      return null;
    }
  });
export const makeSelectStartTimeSelectedService = () =>
  createSelector(selectState, (state: State): string | null =>
    state.startDateTimeSelectedService
      ? getTimeFromDate(new Date(state.startDateTimeSelectedService), true)
      : null
  );
export const makeSelectStartTimeSelectedPreparation = () =>
  createSelector(selectState, (state: State): string | null =>
    state.startDateTimeSelectedPreparation
      ? getTimeFromDate(new Date(state.startDateTimeSelectedPreparation), true)
      : null
  );

export const makeSelectFinishDateTimeSelectedService = () =>
  createSelector(
    selectState,
    (state: State): Date | null => state.finishDateTimeSelectedService
  );
export const makeSelectFinishTimeSelectedServicePristine = () =>
  createSelector(
    selectState,
    (state: State): boolean => state.finishTimeSelectedServicePristine
  );

export const makeHourServiceSelectedService = () =>
  createSelector(
    selectState,
    (state: State): string | null => state.hourService
  );

export const makeSelectFinishTimeStampSelectedService = () =>
  createSelector(
    selectState,
    (state: State): number | null =>
      state.finishDateTimeSelectedService?.getTime() || null
  );
export const makeSelectFinishDateTimeStampSelectedService = () =>
  createSelector(selectState, (state: State): number | null => {
    if (state.finishDateTimeSelectedService) {
      const date: Date = new Date(state.finishDateTimeSelectedService);
      removeTime(date);
      return date.getTime();
    } else {
      return null;
    }
  });
export const makeSelectFinishTimeSelectedService = () =>
  createSelector(selectState, (state: State): string | null =>
    state.finishDateTimeSelectedService
      ? getTimeFromDate(new Date(state.finishDateTimeSelectedService), true)
      : null
  );

export const makeSelectNumTurnoSelected = () =>
  createSelector(selectState, (state: State): string => state.turnSelected);

export const makeSelectTypeServiceSelected = () =>
  createSelector(
    selectState,
    (state: State): string => state.typeServiceSelected
  );

export const makeSelectIdTypePracticeSelected = () =>
  createSelector(
    selectState,
    (state: State): string => state.idTypePracticeSelected
  );

export const makeSelectTypePracticeSelected = () =>
  createSelector(
    selectState,
    (state: State): string => state.typePracticeSelected
  );

export const makeSelectDisposicionPracticeSelected = () =>
  createSelector(
    selectState,
    (state: State): string => state.disposicionPracticeSelected
  );

export const makeSelectTrainSelected = () =>
  createSelector(selectState, (state: State): string => state.trainSelected);

export const makeSelectStationIniSelected = () =>
  createSelector(
    selectState,
    (state: State): Estacion | null => state.stationSelected
  );

export const makeSelectStationFinSelected = () =>
  createSelector(
    selectState,
    (state: State): Estacion | null => state.destinySelected
  );

export const makeSelectStateType = () =>
  createSelector(selectState, (state: State): string => state.stateType);

export const makeSelectVehicleSelected = () =>
  createSelector(selectState, (state: State): string => state.vehicleSelected);

export const makeSelectPracticaEditar = () =>
  createSelector(selectState, (state: State): string => state.practicaEditar);

export const makeSelectMsgSubmit = () =>
  createSelector(selectState, (state: State): string => state.msgSubmit);

export const makeSelectModalDraftType = () =>
  createSelector(selectState, (state: State): string => state.modalDraftType);

export const makeSelectPracticaCreada = () =>
  createSelector(selectState, (state: State): string => state.practicaCreada);

export const makeSelectUserList = () =>
  createSelector(selectState, (state: State): string => state.userList);

export const makeSelectStartJourneySelectedError = () =>
  createSelector(
    selectState,
    (state: State): string | null => state.startJourneySelectedError
  );
export const makeSelectStartJourneySelectedErrorDraft = () =>
  createSelector(
    selectState,
    (state: State): string | null => state.startJourneySelectedErrorDraft
  );
export const makeSelectFinishDateJourneySelectedError = () =>
  createSelector(
    selectState,
    (state: State): string | null => state.finishDateJourneySelectedError
  );

export const makeSelectFinishDateJourneySelectedErrorDraft = () =>
  createSelector(
    selectState,
    (state: State): string | null => state.finishDateJourneySelectedErrorDraft
  );

export const makeSelectFinishTimeJourneySelectedError = () =>
  createSelector(
    selectState,
    (state: State): string | null => state.finishTimeJourneySelectedError
  );
export const makeSelectFinishTimeJourneySelectedErrorDraft = () =>
  createSelector(
    selectState,
    (state: State): string | null => state.finishTimeJourneySelectedErrorDraft
  );
export const makeSelectStartServiceSelectedError = () =>
  createSelector(
    selectState,
    (state: State): string | null => state.startServiceSelectedError
  );

export const makeSelectStartServiceSelectedErrorDraft = () =>
  createSelector(
    selectState,
    (state: State): string | null => state.startServiceSelectedErrorDraft
  );

export const makeSelectStartPreparationSelectedError = () =>
  createSelector(
    selectState,
    (state: State): string | null => state.startPreparationSelectedError
  );

export const makeSelectObservacionesInstructor = () =>
  createSelector(
    selectState,
    (state: State): string => state.observaciones_instructor
  );

export const makeSelectObservacionesAlumno = () =>
  createSelector(
    selectState,
    (state: State): string => state.observaciones_alumno
  );

export const makeSelecetAlert = () =>
  createSelector(selectState, (state: State): Alert => state.alert);

export const makeSelectLoading = () =>
  createSelector(selectState, (state: State): boolean => state.loading);

export const makeSelectFinishDateServiceSelectedError = () =>
  createSelector(
    selectState,
    (state: State): string | null => state.finishDateServiceSelectedError
  );
export const makeSelectFinishDateServiceSelectedErrorDraft = () =>
  createSelector(
    selectState,
    (state: State): string | null => state.finishDateServiceSelectedErrorDraft
  );
export const makeSelectFinishTimeServiceSelectedError = () =>
  createSelector(
    selectState,
    (state: State): string | null => state.finishTimeServiceSelectedError
  );
export const makeSelectFinishTimeServiceSelectedErrorDraft = () =>
  createSelector(
    selectState,
    (state: State): string | null => state.finishTimeServiceSelectedErrorDraft
  );
// MINUTES
// Minutes practice
export const makeSelectMinutesPracticeSelected = () =>
  createSelector(
    selectState,
    (state: State): number | null => state.minutesPracticeSelected
  );
export const makeSelectMinutesPracticeSelectedError = () =>
  createSelector(
    selectState,
    (state: State): string | null => state.minutesPracticeSelectedError
  );
// Minutes ce
export const makeSelectMinutesCESelected = () =>
  createSelector(
    selectState,
    (state: State): number | null => state.minutesCESelected
  );
// Minutes pending ce
export const makeSelectMinutesPendingCESelected = () =>
  createSelector(
    selectState,
    (state: State): number | null => state.minutesPendingCESelected
  );
// Minutes others practices
export const makeSelectMinutesOtherSelected = () =>
  createSelector(
    selectState,
    (state: State): number | null => state.minutesOtherSelected
  );
// Minutes pending others practices
export const makeSelectMinutesPendingOtherSelected = () =>
  createSelector(
    selectState,
    (state: State): number | null => state.minutesPendingOtherSelected
  );
// Minutes Draft
export const makeSelectMinutesDraftSelected = () =>
  createSelector(
    selectState,
    (state: State): number | null => state.minutesDraftSelected
  );
// Minutes PD
export const makeSelectMinutesPreparationSelected = () =>
  createSelector(
    selectState,
    (state: State): number | null => state.minutesPreparationSelected
  );

export const makeSelectIsThereIsAnErrorInJourneySelected = () =>
  createSelector(
    makeSelectStartJourneySelectedError(),
    makeSelectFinishTimeJourneySelectedError(),
    makeSelectFinishDateJourneySelectedError(),
    (err1: string | null, err2: string | null, err3: string | null): boolean =>
      !!err1 || !!err2 || !!err3
  );

export const makeSelectIsThereIsAnErrorInJourneySelectedDraft = () =>
  createSelector(
    makeSelectStartJourneySelectedErrorDraft(),
    makeSelectFinishTimeJourneySelectedErrorDraft(),
    makeSelectFinishDateJourneySelectedErrorDraft(),
    (err1: string | null, err2: string | null, err3: string | null): boolean =>
      !!err1 || !!err2 || !!err3
  );

export const makeSelectIsThereIsAnErrorInServiceSelected = () =>
  createSelector(
    makeSelectStartServiceSelectedError(),
    makeSelectFinishTimeServiceSelectedError(),
    makeSelectFinishDateServiceSelectedError(),
    (err1: string | null, err2: string | null, err3: string | null): boolean =>
      !!err1 || !!err2 || !!err3
  );

export const makeSelectIsThereIsAnErrorInPreparationSelected = () =>
  createSelector(
    makeSelectStartPreparationSelectedError(),
    (err1: string | null): boolean => !!err1
  );

export const makeSelectIsThereIsAnErrorInServiceSelectedDraft = () =>
  createSelector(
    makeSelectStartServiceSelectedErrorDraft(),
    makeSelectFinishTimeServiceSelectedErrorDraft(),
    makeSelectFinishDateServiceSelectedErrorDraft(),
    (err1: string | null, err2: string | null, err3: string | null): boolean =>
      !!err1 || !!err2 || !!err3
  );

export const makeSelectAvise = () =>
  createSelector(
    selectState,
    (state: State): string => state.infoUserProfileStatus
  );

// Formulario crear práctica

// --- Borrador

export const makeSelectAnyErrorForCreateDraft = () =>
  createSelector(
    // Jornada
    makeSelectFinishDateJourneySelectedErrorDraft(),
    makeSelectFinishTimeJourneySelectedErrorDraft(),
    // Servicio
    makeSelectFinishTimeServiceSelectedErrorDraft(),
    // Error preparation time
    makeSelectStartPreparationSelectedError(),
    (
      res1: string | null,
      res2: string | null,
      res3: string | null,
      res4: string | null
    ): boolean => !!res1 || !!res2 || !!res3 || !!res4
  );

export const makeSelectDataIsReadyForCreateDraft = () =>
  createSelector(selectState, (state: State): boolean => {
    return (
      !!state.startDateTimeSelectedJourney &&
      !state.startTimeSelectedJourneyPristine &&
      !!state.finishDateTimeSelectedJourney &&
      !state.finishTimeSelectedJourneyPristine &&
      !!state.startDateTimeSelectedService &&
      !state.startTimeSelectedServicePristine &&
      !!state.finishDateTimeSelectedService &&
      !state.finishTimeSelectedServicePristine &&
      !!state.selectedCourse
    );
  });

export const makeSelectCanCreateDraft = () =>
  createSelector(
    makeSelectUsername(),
    makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
    makeSelectDataIsReadyForCreateDraft(),
    makeSelectAnyErrorForCreateDraft(),
    (
      username: string,
      datosPerfilesUsuario: DatosPerfilesUsuario,
      dataReadyForCreateDraft: boolean,
      errorsForCreateDraft: boolean
    ): boolean => {
      return (
        !!username &&
        !!datosPerfilesUsuario?.ID_PERFIL &&
        dataReadyForCreateDraft &&
        !errorsForCreateDraft
      );
    }
  );

// --- Práctica para validar

export const makeSelectAnyErrorForCreatePractice = () =>
  createSelector(
    // Jornada
    makeSelectStartJourneySelectedErrorDraft(),
    makeSelectFinishDateJourneySelectedErrorDraft(),
    makeSelectFinishTimeJourneySelectedErrorDraft(),
    // Puesta a disposición
    makeSelectStartPreparationSelectedError(),
    // Servicio
    makeSelectStartServiceSelectedErrorDraft(),
    makeSelectFinishTimeServiceSelectedErrorDraft(),
    (
      res1: string | null,
      res2: string | null,
      res3: string | null,
      res4: string | null,
      res5: string | null,
      res6: string | null
    ): boolean => !!res1 || !!res2 || !!res3 || !!res4 || !!res5 || !!res6
  );

export const makeSelectDataIsReadyForCreatePractice = () =>
  createSelector(
    makeSelectDataIsReadyForCreateDraft(), // Jornada, servicio y curso
    selectState,
    (dataIsReadyForCreateDraft: boolean, state: State): boolean => {
      return (
        dataIsReadyForCreateDraft &&
        (state.startTimeSelectedPreparationPristine ||
          !!state.startDateTimeSelectedPreparation) &&
        !!state.stationSelected &&
        !!state.destinySelected &&
        !!state.selectedInstructors?.length &&
        !!state.idTypePracticeSelected &&
        !!state.typeServiceSelected &&
        !!state.vehicleSelected &&
        !!state.trainSelected &&
        !!state.turnSelected
      );
    }
  );

export const makeSelectCanCreatePractice = () =>
  createSelector(
    makeSelectUsername(),
    makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
    makeSelectDataIsReadyForCreatePractice(),
    makeSelectAnyErrorForCreatePractice(),
    (
      username: string,
      datosPerfilesUsuario: DatosPerfilesUsuario,
      dataIsReadyForCreatePractice: boolean,
      errorsForCreatePractice: boolean
    ): boolean => {
      return (
        !!username &&
        !!datosPerfilesUsuario?.ID_PERFIL &&
        dataIsReadyForCreatePractice &&
        !errorsForCreatePractice
      );
    }
  );

export const makeSelectIsAuditar = () =>
  createSelector(selectState, (state: State): string => state.isAuditar);
